import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useToast } from '../hooks/useToast';
import DataTable from '../components/common/DataTable';
import ConfirmModal from '../components/common/ConfirmModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import '../styles/WorkersPage.css';
import {
  getWorkers,
  getPendingApprovals,
  getWorkerPerformance,
  deactivateWorker,
  reactivateWorker,
  getWorkerResponseTimes,
} from '../utils/api';
import {
  Briefcase,
  UserCheck,
  UserX,
  Clock,
  Star,
  TrendingUp,
  Eye,
  Trophy,
  ShieldCheck,
  Activity,
  AlertTriangle
} from 'lucide-react';

const WorkersPage = () => {
  const navigate = useNavigate();
  const goProfile = (row) => navigate(`/admin/profile/${row.id || row.user_id}`);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'all';
  const [activeTab, setActiveTab] = useState(initialTab);
  const { success, error } = useToast();
  const [confirmModal, setConfirmModal] = useState({ open: false, action: null, worker: null });

  const { data: workers, isLoading: workersLoading, execute: fetchWorkers } = useApi(getWorkers, { immediate: true });
  const { data: pendingApprovals, isLoading: pendingLoading } = useApi(getPendingApprovals, { immediate: true });
  const { data: performance, isLoading: perfLoading } = useApi(getWorkerPerformance, { immediate: true });
  const { data: responseTimes } = useApi(getWorkerResponseTimes, { immediate: true });

  const handleDeactivate = async (workerId) => {
    try {
      await deactivateWorker(workerId);
      success('Profissional desativado com sucesso!');
      fetchWorkers();
      setConfirmModal({ open: false, action: null, worker: null });
    } catch (err) {
      error('Erro ao desativar profissional');
    }
  };

  const handleReactivate = async (workerId) => {
    try {
      await reactivateWorker(workerId);
      success('Profissional reativado com sucesso!');
      fetchWorkers();
      setConfirmModal({ open: false, action: null, worker: null });
    } catch (err) {
      error('Erro ao reativar profissional');
    }
  };

  const openConfirm = (action, worker) => {
    setConfirmModal({ open: true, action, worker });
  };

  const tabs = [
    { id: 'all', label: 'Todos', count: (workers || []).length },
    { id: 'pending', label: 'Pendentes', count: (pendingApprovals || []).length },
    { id: 'active', label: 'Ativos', count: (workers || []).filter(w => w.is_active !== false).length },
    { id: 'inactive', label: 'Inativos', count: (workers || []).filter(w => w.is_active === false).length },
    { id: 'performance', label: 'Performance', count: null },
    { id: 'ranking', label: 'Ranking', count: null },
  ];

  // Ranking: melhores primeiro (nota, depois nº de serviços)
  const rankedWorkers = [...(performance || [])].sort((a, b) =>
    (b.rating || 0) - (a.rating || 0) || (b.completed_jobs || 0) - (a.completed_jobs || 0)
  );

  const statusBadge = (status) => (
    <span className={`px-2 py-1 rounded-full text-xs font-medium
      ${status === 'verified' || status === true ? 'bg-green-500/20 text-green-400' :
        status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
        'bg-red-500/20 text-red-400'}`}>
      {status === 'verified' || status === true ? 'Verificado' : status === 'pending' ? 'Pendente' : 'Inativo'}
    </span>
  );

  const ratingStars = (rating) => {
    const stars = Math.round(rating || 0);
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star key={i} className={`w-3.5 h-3.5 ${i <= stars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} />
        ))}
        <span className="ml-1 text-sm text-gray-400">({rating || 0})</span>
      </div>
    );
  };

  const allColumns = [
    { key: 'id', label: 'ID', width: '60px' },
    { key: 'full_name', label: 'Nome', render: (row) => (
      <button onClick={() => goProfile(row)} className="text-white hover:text-[#00f1fe] font-medium transition-colors text-left">{row.full_name}</button>
    ) },
    { key: 'email', label: 'Email' },
    { key: 'category', label: 'Categoria' },
    { key: 'rating', label: 'Avaliação', render: (row) => ratingStars(row.rating) },
    { key: 'status', label: 'Status', render: (row) => statusBadge(row.registration_status || row.is_active) },
    { key: 'response_time', label: 'Tempo Resp.', render: (row) => {
      const rt = responseTimes?.find(r => r.worker_id === row.id);
      return rt ? `${(rt.avg_response_time / 60).toFixed(1)} min` : '-';
    }},
    { key: 'completed_jobs', label: 'Serviços', render: (row) => row.completed_jobs || 0 },
    {
      key: 'actions',
      label: 'Ações',
      sortable: false,
      render: (row) => (
        <div className="flex items-center gap-2">
          <button onClick={() => goProfile(row)} className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors" title="Ver perfil">
            <Eye className="w-4 h-4" />
          </button>
          {row.is_active !== false ? (
            <button
              onClick={() => openConfirm('deactivate', row)}
              className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
              title="Desativar"
            >
              <UserX className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => openConfirm('reactivate', row)}
              className="p-1.5 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors"
              title="Reativar"
            >
              <UserCheck className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const pendingColumns = [
    { key: 'user_id', label: 'ID', width: '60px' },
    { key: 'full_name', label: 'Nome', render: (row) => (
      <button onClick={() => goProfile(row)} className="text-white hover:text-[#00f1fe] font-medium transition-colors text-left">{row.full_name}</button>
    ) },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Telefone' },
    { key: 'registration_status', label: 'Status', render: (row) => statusBadge(row.registration_status) },
    { key: 'submitted_at', label: 'Submetido', render: (row) => 
      row.submitted_at ? new Date(row.submitted_at).toLocaleDateString('pt-PT') : '-'
    },
    {
      key: 'actions',
      label: 'Ações',
      sortable: false,
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400 max-w-[220px]">
            A activação faz-se no percurso, depois da entrevista e da formação.
          </span>
          <button onClick={() => goProfile(row)} className="workers-profile-action">
            <Eye className="w-4 h-4" />
            Ver perfil
          </button>
        </div>
      ),
    },
  ];

  const performanceColumns = [
    { key: 'id', label: 'ID' },
    { key: 'full_name', label: 'Nome', render: (row) => (
      <button onClick={() => goProfile(row)} className="text-white hover:text-[#00f1fe] font-medium transition-colors text-left">{row.full_name}</button>
    ) },
    { key: 'completed_jobs', label: 'Serviços Completos' },
    { key: 'rating', label: 'Avaliação', render: (row) => ratingStars(row.rating) },
    { key: 'response_time', label: 'Tempo Médio Resp.', render: (row) => {
      const rt = responseTimes?.find(r => r.worker_id === row.id);
      return rt ? `${(rt.avg_response_time / 60).toFixed(1)} min` : '-';
    }},
    { key: 'earnings', label: 'Ganhos', render: (row) => `Kz ${Number(row.earnings || 0).toLocaleString('pt-PT')}` },
    { key: 'acceptance_rate', label: 'Taxa Aceitação', render: (row) => `${(row.acceptance_rate || 0)}%` },
  ];

  const rankingColumns = [
    {
      key: 'position', label: '#', width: '70px', sortable: false,
      render: (row) => {
        const pos = rankedWorkers.findIndex(w => w.id === row.id) + 1;
        return <span className={`worker-rank worker-rank--${pos <= 3 ? pos : 'other'}`}>{pos}</span>;
      },
    },
    { key: 'full_name', label: 'Nome', render: (row) => (
      <button onClick={() => goProfile(row)} className="text-white hover:text-[#00f1fe] font-medium transition-colors text-left">{row.full_name}</button>
    ) },
    { key: 'rating', label: 'Avaliação', render: (row) => ratingStars(row.rating) },
    { key: 'completed_jobs', label: 'Serviços', render: (row) => row.completed_jobs || 0 },
    {
      key: 'trend', label: 'Destaque', sortable: false,
      render: (row) => {
        const pos = rankedWorkers.findIndex(w => w.id === row.id) + 1;
        const total = rankedWorkers.length;
        if (pos <= 3 && (row.rating || 0) > 0) {
          return (
            <span className="flex items-center gap-1 text-green-400 text-xs font-medium">
              <TrendingUp className="w-3.5 h-3.5" /> Em destaque
            </span>
          );
        }
        if (total > 5 && pos > total - 3) {
          return <span className="flex items-center gap-1 text-red-400 text-xs font-medium"><AlertTriangle className="w-3.5 h-3.5" /> Precisa melhorar</span>;
        }
        return <span className="text-gray-500 text-xs">—</span>;
      },
    },
  ];

  const getTabData = () => {
    switch (activeTab) {
      case 'pending':
        return { data: pendingApprovals || [], columns: pendingColumns };
      case 'active':
        return { data: (workers || []).filter(w => w.is_active !== false), columns: allColumns };
      case 'inactive':
        return { data: (workers || []).filter(w => w.is_active === false), columns: allColumns };
      case 'performance':
        return { data: performance || [], columns: performanceColumns };
      case 'ranking':
        return { data: rankedWorkers, columns: rankingColumns };
      default:
        return { data: workers || [], columns: allColumns };
    }
  };

  const { data: tabData, columns: tabColumns } = getTabData();

  const isLoading = workersLoading || pendingLoading || perfLoading;
  const activeWorkers = (workers || []).filter(w => w.is_active !== false).length;
  const activeRate = (workers || []).length ? Math.round((activeWorkers / workers.length) * 100) : 0;
  const completedJobs = performance?.reduce((acc, w) => acc + Number(w.completed_jobs || 0), 0) || 0;

  return (
    <div className="workers-admin-page space-y-6">
      <section className="workers-admin-hero">
        <div className="workers-admin-hero__copy">
          <span className="workers-admin-eyebrow"><ShieldCheck className="w-4 h-4" /> Rede de profissionais</span>
          <h1>Profissionais</h1>
          <p>Acompanhe a qualidade, aprove novos talentos e mantenha a rede Dozy pronta para cada pedido.</p>
        </div>
        <div className="workers-network-health">
          <div className="workers-network-health__icon"><Activity className="w-6 h-6" /></div>
          <div><strong>{activeRate}%</strong><span>da rede está ativa</span></div>
          <i aria-hidden="true" style={{ '--network-rate': `${activeRate}%` }} />
        </div>
      </section>

      <div className="workers-admin-stats">
        <article className="workers-stat workers-stat--blue">
          <div className="workers-stat__icon"><Briefcase /></div>
          <div><span>Rede total</span><strong>{(workers || []).length}</strong><small>profissionais registados</small></div>
        </article>
        <article className="workers-stat workers-stat--amber">
          <div className="workers-stat__icon"><Clock /></div>
          <div><span>A aguardar</span><strong>{(pendingApprovals || []).length}</strong><small>perfis por validar</small></div>
        </article>
        <article className="workers-stat workers-stat--green">
          <div className="workers-stat__icon"><UserCheck /></div>
          <div><span>Disponíveis</span><strong>{activeWorkers}</strong><small>profissionais ativos</small></div>
        </article>
        <article className="workers-stat workers-stat--violet">
          <div className="workers-stat__icon"><TrendingUp /></div>
          <div><span>Concluídos</span><strong>{completedJobs}</strong><small>serviços realizados</small></div>
        </article>
      </div>

      <div className="workers-admin-tabs-shell">
        <div className="workers-admin-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? 'is-active' : ''}
            >
              {tab.id === 'ranking' && <Trophy className="w-4 h-4" />}
              {tab.label}
              {tab.count !== null && (
                <span>{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <section className="workers-admin-table">
      {isLoading && !workers ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <DataTable
          data={tabData}
          columns={tabColumns}
          pageSize={10}
          emptyMessage={activeTab === 'pending' ? 'Nenhuma aprovação pendente.' : 'Nenhum profissional encontrado.'}
          rowClassName={activeTab === 'ranking' ? (row) => {
            const pos = rankedWorkers.findIndex(w => w.id === row.id) + 1;
            if (pos <= 3 && (row.rating || 0) > 0) return 'bg-green-500/5';
            if (rankedWorkers.length > 5 && pos > rankedWorkers.length - 3) return 'bg-red-500/5';
            return '';
          } : undefined}
        />
      )}
      </section>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.action === 'deactivate' ? 'Desativar profissional' : 'Reativar profissional'}
        message={`Tem certeza que deseja ${confirmModal.action === 'deactivate' ? 'desativar' : 'reativar'} o profissional "${confirmModal.worker?.full_name}"?`}
        onConfirm={() => {
          const wid = confirmModal.worker?.user_id || confirmModal.worker?.id;
          if (confirmModal.action === 'deactivate') handleDeactivate(wid);
          else handleReactivate(wid);
        }}
        onCancel={() => setConfirmModal({ open: false, action: null, worker: null })}
        confirmText={confirmModal.action === 'deactivate' ? 'Desativar' : 'Reativar'}
        type={confirmModal.action === 'deactivate' ? 'danger' : 'success'}
      />
    </div>
  );
};

export default WorkersPage;
